import { useMemo } from 'react';
import { getDimensionsWithGeometry } from 'src/utils/3d.utils';
import { useStocksByPieceId } from 'src/api/stock/getStocksByPieceId';
import { useStocksByStele } from 'src/api/stock/getStocksByStele';
import { useAssemblyStockWithoutGranit } from 'src/api/stock/getAssemblyStockWithoutGranit';

export const useTextureStock = (
  piece: any,
  pieces: any[],
  type: string,
  history: Record<string, Record<string, Float32Array>>,
) => {
  // history changes ref when changeGeometry is called (via setHistory),
  // so we use it as a trigger to recompute geometry-dependent keys
  const historyKey = Object.keys(history).length;

  // Create a stable key for piece geometry to avoid infinite re-renders
  const pieceGeometryKey = piece?.geometry?.attributes?.position?.array
    ? JSON.stringify(
        Array.from(piece.geometry.attributes.position.array.slice(0, 10)),
      )
    : '';

  const dimensions = useMemo(
    () =>
      piece?.geometry?.attributes?.position?.array
        ? getDimensionsWithGeometry(piece.geometry.attributes.position.array)
        : undefined,
    [pieceGeometryKey, historyKey],
  );

  const { data: stockData } = useStocksByPieceId(
    type === 'activePieces' && piece ? piece.id : undefined,
    type === 'activePieces' && piece ? dimensions : undefined,
  );

  const steleParams = useMemo(
    () =>
      type === 'customStele' && piece
        ? {
            baseIndex: Number(piece.baseIndex),
            boxDownWidth: Number(piece.boxDownWidth),
            boxHeight: Number(piece.boxHeight),
            boxLeftHeight: Number(piece.boxLeftHeight),
            boxRightHeight: Number(piece.boxRightHeight),
            boxUpWidth: Number(piece.boxUpWidth),
            headIndex: Number(piece.headIndex),
            depth: Number(piece.depth),
          }
        : undefined,
    [
      piece?.id,
      type,
      piece?.baseIndex,
      piece?.boxDownWidth,
      piece?.boxHeight,
      piece?.boxLeftHeight,
      piece?.boxRightHeight,
      piece?.boxUpWidth,
      piece?.headIndex,
      piece?.depth,
    ],
  );

  const { data: stockStelesData } = useStocksByStele(steleParams);

  // Create a stable key for pieces (IDs + dimensions) to refetch when geometry changes
  const piecesKey = pieces
    .map((el) => {
      if (!el.geometry?.attributes?.position?.array) return `${el.id}`;
      const { x, y, z } = getDimensionsWithGeometry(
        el.geometry.attributes.position.array,
      );
      return `${el.id}:${x}:${y}:${z}`;
    })
    .join('|');

  const assemblyBody = useMemo(
    () =>
      type === 'activePieces' && pieces.length > 0
        ? {
            stockPieces: pieces
              .filter((el) => el?.geometry?.attributes?.position?.array)
              .map((el) => {
                const { x, y, z } = getDimensionsWithGeometry(
                  el.geometry.attributes.position.array,
                );

                return {
                  pieceId: el.id ?? -1,
                  newGeometry: [...el.geometry.attributes.position.array],
                  x,
                  y,
                  z,
                };
              }),
          }
        : undefined,
    [piecesKey, type, historyKey],
  );

  const { data: assemblyStockData, refetch: refreshAssemblyStock } =
    useAssemblyStockWithoutGranit(assemblyBody);

  return {
    stockData,
    stockStelesData,
    assemblyStockData,
    refreshAssemblyStock,
  };
};
